/**
 * Display helpers for property facts. A fact value is arbitrary JSON, so only the
 * shapes with a plain-text rendering are shown: text reads as-is, booleans read
 * Yes/No, and a list of strings reads as one comma-separated line. `ami_income_limits`
 * holds objects and so falls through — the AMI bands card renders it on its own.
 */

import type { IProperty, IPropertyFact } from 'src/services/api/properties/types';
import {
    isBooleanPropertyFact,
    isStringPropertyFact,
} from 'src/services/api/properties/property-fact-values';
import { PROPERTY_FACT_KEY_LABELS } from 'src/services/api/properties/property-fact-keys';

export type DisplayedPropertyFact = {
    key: string;
    label: string;
    value: string;
};

function displayValue(fact: IPropertyFact): string | null {
    if (isStringPropertyFact(fact)) {
        return fact.value;
    }
    if (isBooleanPropertyFact(fact)) {
        return fact.value ? 'Yes' : 'No';
    }
    if (Array.isArray(fact.value) && fact.value.length > 0 &&
        fact.value.every((entry) => typeof entry === 'string')) {
        return fact.value.join(', ');
    }
    return null;
}

/** The facts shown in a sidebar section, in the order the API returned them. */
export function getDisplayedPropertyFacts(facts: IProperty['facts']): DisplayedPropertyFact[] {
    return Object.values(facts ?? {}).flatMap((fact) => {
        const value = displayValue(fact);
        if (value === null) {
            return [];
        }
        return [
            {
                key: fact.key,
                label: PROPERTY_FACT_KEY_LABELS[fact.key] ?? fact.key,
                value,
            },
        ];
    });
}
